import React, { useCallback, useState } from 'react';
import { Row, Col, Button } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import TaskBoardContainer from './board/TaskBoardContainer';
import { PageHeaderLayout, PageContentLayout } from '../../../shared/layout';
import { withRouter, useLocation, useHistory, Switch, Route } from 'react-router-dom';
import UnassignedTasksView from './UnassignedTasksView';
import { HasAccess } from '@permify/react-role';
import { space, bg, fontSize } from '../configs/designTokens';
import '../styles/index.scss';
import TaskOverdueChart from './TaskOverdueChart';
import { ProjectBoard } from './projects';
import { TaskAnalytics } from './analytics';
import { TaskAdmin } from './admin';
import useResponsiveTask from '../hooks/useResponsiveTask';
import { TaskErrorBoundary, SmartFilterChips, ViewTabs, ScopeSelector } from './shared';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import HelpCenterModal from '../../../shared/components/HelpCenterModal/HelpCenterModal';
import { buildUserActions } from '../../../../core/utils/helpCenter/buildUserActions';

// Set Document Title
document.title = `Task Management`;

const Task = (props) => {
  const location = useLocation();
  const history = useHistory();
  const { width: windowWidth, isMobile } = useResponsiveTask();
  const { isKanban, setIsKanban, setViewChanged } = useTaskContext();
  const [helpVisible, setHelpVisible] = useState(false);

  // Determine current task view from URL
  const getCurrentTaskView = useCallback(() => {
    if (location.pathname.includes('/tasks/unassigned')) return 'unassigned';
    if (location.pathname.includes('/tasks/overdue')) return 'overdue';
    return 'main';
  }, [location.pathname]);

  const currentTaskView = getCurrentTaskView();

  // Convert isKanban boolean to view type string
  const viewType = isKanban ? 'board' : 'table';

  const isPlatformLevel = props.currentUser?.role?.org?.level === 'PLATFORM';
  const userActions = buildUserActions(
    props.currentUser?.permission,
    props.currentUser?.features,
    props.currentUser?.role?.org?.level
  );

  // Handle view type changes (Board/Table)
  // setViewChanged(true) clears stale task data before the refetch
  const handleViewTypeChange = (view) => {
    setIsKanban(view === 'board');
    setViewChanged(true);
  };

  // Handle smart filter navigation (links to separate views)
  // If clicking the currently active filter, navigate back to main view
  const handleSmartFilterClick = (filters) => {
    // No active filters — navigate back to main view
    if (filters.length === 0) {
      history.push('/tasks/main/1');
      return;
    }

    const clickedFilter = filters[filters.length - 1];

    switch(clickedFilter) {
      case 'overdue':
        history.push('/tasks/overdue/');
        break;
      case 'unassigned':
        history.push('/tasks/unassigned/1');
        break;
      case 'dueToday':
        // Due today stays on main view with filter (future enhancement)
        history.push('/tasks/main/1');
        break;
      default:
        history.push('/tasks/main/1');
    }
  };

  // Get active filters based on current view
  const getActiveFilters = () => {
    if (currentTaskView === 'overdue') return ['overdue'];
    if (currentTaskView === 'unassigned') return ['unassigned'];
    return [];
  };

  // Handle scope changes from ScopeSelector
  const handleScopeChange = (scope, route) => {
    if (route) {
      history.push(route);
    }
  };

  // Determine current scope from URL
  const getCurrentScope = useCallback(() => {
    if (location.pathname.includes('/tasks/projects')) return 'projects';
    if (location.pathname.includes('/tasks/analytics')) return 'analytics';
    if (location.pathname.includes('/tasks/admin')) return 'admin';
    return 'tasks';
  }, [location.pathname]);

  return (
    <>
      <PageHeaderLayout
        title="TASKS"
        style={{
          fontSize: fontSize.display,
          border: 'none',
          paddingTop: '2vh',
        }}
        rightContent={
          <Button
            type="default"
            shape="round"
            icon={<QuestionCircleOutlined />}
            onClick={() => setHelpVisible(true)}
          >
            Help Center
          </Button>
        }
      />
      <HelpCenterModal
        visible={helpVisible}
        onClose={() => setHelpVisible(false)}
        feature="TASKS"
        title="Tasks Help Center"
        userActions={userActions}
      />
      <PageContentLayout style={{ backgroundColor: bg.card, paddingInline: '2%' }}>
        {isPlatformLevel ? (
          <HasAccess roles={['PLATFORM']}>
            {/* Phase 4: Consolidated Navigation Header */}
            <Row gutter={[16, 16]} style={{ marginBottom: space[4] }} align="middle">
              <Col xs={24} sm={12} md={8} lg={6}>
                <ScopeSelector
                  activeScope={getCurrentScope()}
                  onScopeChange={handleScopeChange}
                  compact={windowWidth < 425}
                />
              </Col>
            </Row>

            {/* Smart Filter Chips + View Tabs in one row (hidden for analytics scope) */}
            {getCurrentScope() === 'tasks' && (
              <Row style={{ marginBottom: space[4] }} align="middle">
                <Col flex="1">
                  <SmartFilterChips
                    activeFilters={getActiveFilters()}
                    onFilterChange={handleSmartFilterClick}
                    showDueToday={false}
                  />
                </Col>
                {currentTaskView === 'main' && (
                  <Col flex="none">
                    <ViewTabs
                      activeView={viewType}
                      onViewChange={handleViewTypeChange}
                      availableViews={['board', 'table']}
                      showIcons={true}
                      compact={isMobile}
                    />
                  </Col>
                )}
              </Row>
            )}

            <TaskErrorBoundary>
              <Switch>
                <Route path="/tasks/projects" exact component={ProjectBoard} />
                <Route path="/tasks/analytics" exact component={TaskAnalytics} />
                <Route path="/tasks/admin" exact component={TaskAdmin} />
                <Route path="/tasks/unassigned/:pageNumber?" exact component={UnassignedTasksView} />
                <Route path="/tasks/overdue/" exact component={TaskOverdueChart} />
                <Route path="/tasks/main/:pageNumber?" exact component={TaskBoardContainer} />
              </Switch>
            </TaskErrorBoundary>
          </HasAccess>
        ) : (
          <TaskErrorBoundary>
            <Row style={{ marginBottom: space[4] }} align="middle" justify="end">
              <Col flex="none">
                <ViewTabs
                  activeView={viewType}
                  onViewChange={handleViewTypeChange}
                  availableViews={['board', 'table']}
                  showIcons={true}
                  compact={isMobile}
                />
              </Col>
            </Row>
            <TaskBoardContainer />
          </TaskErrorBoundary>
        )}
      </PageContentLayout>
    </>
  );
};

export default withRouter(Task);
